import type { ProfileDoc } from '../contract.js';
import { copy } from '../copy.js';
import { hasContent, peDirty } from './derive.js';
import { isYou, meOf, profileOfMember } from './lookup.js';
import { homeNow } from './nav.js';
import { uiError } from './store.js';
import type { AppStore } from './store.js';
import type { Patch } from './types.js';

/** Editing your own profile (03 §3.14, 04 §13). It is a mode of the profile view, not a view. */

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const CLOSED: Patch = { sel: null, txt: null, tray: null, pdrag: null, dropCell: null };

/** Island Edit profile: your profile only, and only once it has loaded. */
export function openEdit(s: AppStore): void {
  const st = s.getState();
  if (st.lv || st.pe || st.view !== 'profile' || !isYou(st, st.profileOf)) return;
  const saved = profileOfMember(st, st.profileOf);
  if (!saved) return;
  s.stopPlayback();
  s.resetPointer();
  const me = meOf(st);
  // The session is the truth for name, colour and picture; the profile for description, grid and lines.
  const pe: ProfileDoc = clone({ ...saved, name: me.name, color: me.color, pic: me.pic });
  s.set({ pe, peBase: JSON.stringify(pe), peStatus: 'idle', peAsk: false, ...CLOSED, uploading: {}, play: null });
}

/** Leave editing at once and drop the changes. */
export function closeEdit(s: AppStore): void {
  s.resetPointer();
  s.set({ pe: null, peBase: '', peStatus: 'idle', peAsk: false, ...CLOSED, uploading: {} });
}

/** Island Cancel: asks first when something changed. */
export function peCancel(s: AppStore): void {
  const st = s.getState();
  if (!st.pe || st.peStatus === 'saving') return;
  if (peDirty(st)) { s.resetPointer(); s.set({ peAsk: true, homeAfter: false, tray: null, sel: null, txt: null, pdrag: null }); return; }
  closeEdit(s);
}

/** Island Done / Try again. A blank name keeps the previous one; empty texts are dropped. */
export async function peDone(s: AppStore): Promise<void> {
  const st = s.getState();
  if (!st.pe || st.peStatus === 'saving') return;
  if (!peDirty(st)) { closeEdit(s); return; }
  s.resetPointer();
  s.set({ peStatus: 'saving', tray: null, pdrag: null, sel: null, txt: null });
  try {
    const failures = s.uploadFailures;
    await s.uploadsSettled();
    const now = s.getState();
    if (!now.pe) return;
    if (s.uploadFailures !== failures) { s.set({ peStatus: 'failed' }); return; }
    const doc: ProfileDoc = clone(now.pe);
    doc.name = (doc.name || '').trim() || meOf(now).name;
    doc.items = doc.items.filter(hasContent);
    const stored = await s.data.saveProfile(doc);
    s.patchOwnProfile(stored);
    // Your name, colour and picture show in the island, posts and stripes (06 §5.5).
    await s.refreshSession();
    s.set({ pe: null, peBase: '', peStatus: 'idle', peAsk: false, ...CLOSED, uploading: {} });
    s.flash(copy.toast.profileSaved);
  } catch (reason) {
    s.report(uiError(reason));
    if (s.getState().pe) s.set({ peStatus: 'failed' });
  }
}

/** The profile prompt's Discard: leave editing, or go home when Home raised the prompt. */
export function confirmProfile(s: AppStore): void {
  if (s.getState().homeAfter) { homeNow(s); return; }
  closeEdit(s);
}

/** Look: the island button or your picture in the header. */
export function toggleLook(s: AppStore, fromPicture = false): void {
  const st = s.getState();
  if (!st.pe || st.peStatus === 'saving') return;
  s.set({ tray: st.tray === 'look' ? null : 'look', sel: null, txt: null, ...(fromPicture ? { swOpen: false } : {}) });
}
